import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import MachineCard from "@/components/MachineCard";
import AddMachineDialog from "@/components/AddMachineDialog";
import { InstallerDialog } from "@/components/InstallerDialog";
import { UpdateAgentDialog } from "@/components/UpdateAgentDialog";
import SupportDialog from "@/components/SupportDialog";
import { AssignCustomerDialog } from "@/components/support/AssignCustomerDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Desktop, Lifebuoy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Dashboard() {
  const { currentOrg, currentOrgId } = useAuth();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [regenTarget, setRegenTarget] = useState(null);
  const [installerTarget, setInstallerTarget] = useState(null);
  const [updateTarget, setUpdateTarget] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);
  const [snapQuota, setSnapQuota] = useState(null);

  const loadSnapQuota = useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const r = await api.get(`/orgs/${currentOrgId}/support/quota`);
      setSnapQuota(r.data);
    } catch { /* ignore */ }
  }, [currentOrgId]);

  useEffect(() => { loadSnapQuota(); }, [loadSnapQuota]);

  useEffect(() => {
    api.get("/agent/version").then((r) => setLatestVersion(r.data?.latest)).catch(() => {});
  }, []);

  const canManage = currentOrg && (currentOrg.role === "owner" || currentOrg.role === "admin");
  const machineLimit = currentOrg?.limits?.max_machines;

  const load = useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const res = await api.get(`/orgs/${currentOrgId}/machines`);
      setMachines(res.data);
    } catch {
      toast.error("Could not load machines");
    } finally {
      setLoading(false);
    }
  }, [currentOrgId]);

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const cancelUninstall = async (m) => {
    try {
      await api.post(`/orgs/${currentOrgId}/machines/${m.machine_id}/cancel-uninstall`);
      toast.success(`Cancelled scheduled uninstall for ${m.name}`);
      load();
    } catch {
      toast.error("Could not cancel — it may already be uninstalling");
    }
  };

  const confirmDelete = async (opts = {}) => {
    const m = deleteTarget;
    setDeleteTarget(null);
    const params = new URLSearchParams({ uninstall: "true" });
    if (opts.offline_action) params.set("offline_action", opts.offline_action);
    try {
      const res = await api.delete(`/orgs/${currentOrgId}/machines/${m.machine_id}?${params.toString()}`);
      const status = res.data?.status;
      if (status === "uninstalling") toast.success(`Uninstalling the agent on ${m.name}…`);
      else if (status === "scheduled") toast.success(`Uninstall scheduled — ${m.name} will be cleaned up when it comes online`);
      else if (status === "removed_left_agent") toast.success(`Removed ${m.name} (agent is still installed on that PC)`);
      else toast.success(`Removed ${m.name}`);
      load();
    } catch {
      toast.error("Failed to remove machine");
    }
  };

  const openRename = (m) => {
    setRenameTarget(m);
    setRenameValue(m.name || "");
  };

  const confirmRename = async () => {
    const m = renameTarget;
    const name = renameValue.trim();
    if (!name || name === m.name) { setRenameTarget(null); return; }
    setRenaming(true);
    try {
      await api.patch(`/orgs/${currentOrgId}/machines/${m.machine_id}`, { name });
      toast.success(`Renamed to "${name}"`);
      setRenameTarget(null);
      load();
    } catch {
      toast.error("Could not rename machine");
    } finally {
      setRenaming(false);
    }
  };

  const confirmRegenerate = async () => {
    const m = regenTarget;
    setRegenTarget(null);
    try {
      const res = await api.post(`/orgs/${currentOrgId}/machines/${m.machine_id}/regenerate-token`);
      toast.success(`New token issued for ${m.name}. The old installer no longer works.`);
      load();
      setInstallerTarget(res.data);
    } catch {
      toast.error("Failed to regenerate token");
    }
  };

  const refreshInstallLink = async (m) => {
    const res = await api.post(`/orgs/${currentOrgId}/machines/${m.machine_id}/regenerate-token`);
    setInstallerTarget(res.data);
    load();
    toast.success("Fresh install link generated. Valid for 15 minutes.");
    return res.data;
  };

  const onlineCount = machines.filter((m) => m.online).length;
  const atLimit = machineLimit != null && machines.length >= machineLimit;

  return (
    <AppShell>
      <div className="px-6 py-8 max-w-7xl w-full mx-auto">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Machines</h1>
            <p className="text-sm text-zinc-400 mt-1">
              {machines.length}{machineLimit != null ? ` / ${machineLimit}` : ""} registered
              {" · "}<span className="text-emerald-400">{onlineCount} online</span>
              {currentOrg && <span className="text-zinc-600"> · {currentOrg.name}</span>}
            </p>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <div className="flex flex-col items-end">
                <Button
                  data-testid="remote-support-button"
                  variant="outline"
                  onClick={() => setSupportOpen(true)}
                  className="border-white/15 bg-transparent hover:bg-zinc-800"
                >
                  <Lifebuoy size={18} weight="bold" className="mr-1.5" /> Controlly Snap
                </Button>
                {snapQuota && (
                  <span data-testid="snap-quota-badge" className="mt-1.5 text-[11px] font-mono">
                    {snapQuota.unlimited ? (
                      <span className="text-emerald-400">{snapQuota.included ? (currentOrg?.plan === "internal" ? "Unlimited (Internal)" : "Unlimited (Team)") : "Unlimited (add-on)"}</span>
                    ) : (
                      <span className={snapQuota.remaining === 0 ? "text-amber-400" : "text-zinc-500"}>
                        {snapQuota.remaining} of {snapQuota.limit} trials left
                      </span>
                    )}
                  </span>
                )}
              </div>
              <Button
                data-testid="add-machine-button"
                onClick={() => atLimit ? toast.error(`You've reached your plan limit of ${machineLimit} machines.`) : setAddOpen(true)}
                className="bg-blue-600 hover:bg-blue-500"
              >
                <Plus size={18} weight="bold" className="mr-1.5" /> Add machine
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono text-sm animate-pulse">Loading machines…</div>
        ) : machines.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-lg py-20 flex flex-col items-center justify-center text-center">
            <div className="w-14 h-14 rounded-lg bg-zinc-900 flex items-center justify-center mb-4">
              <Desktop size={28} className="text-zinc-500" />
            </div>
            <h3 className="font-heading text-lg font-semibold">No machines yet</h3>
            <p className="text-sm text-zinc-400 mt-1 max-w-sm">
              {canManage
                ? "Add a machine to get a pairing token, then run the agent on that computer to control it from here."
                : "No machines have been assigned to you yet. Ask a team admin to give you access."}
            </p>
            {canManage && (
              <Button data-testid="add-first-machine-button" onClick={() => setAddOpen(true)} className="mt-6 bg-blue-600 hover:bg-blue-500">
                <Plus size={18} weight="bold" className="mr-1.5" /> Add your first machine
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {machines.map((m) => (
              <MachineCard key={m.machine_id} machine={m} onDelete={setDeleteTarget} onRename={openRename} onShowInstaller={setInstallerTarget} onShowUpdate={setUpdateTarget} onRegenerate={setRegenTarget} onCancelUninstall={cancelUninstall} onAssignCustomer={setAssignTarget} canManage={canManage} latestVersion={latestVersion} />
            ))}
          </div>
        )}
      </div>

      <AddMachineDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} orgId={currentOrgId} />
      <SupportDialog open={supportOpen} onOpenChange={(v) => { setSupportOpen(v); if (!v) loadSnapQuota(); }} />
      <AssignCustomerDialog open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)} machine={assignTarget} onAssigned={load} />

      <InstallerDialog open={!!installerTarget} onOpenChange={(v) => !v && setInstallerTarget(null)} machine={installerTarget} onRefresh={refreshInstallLink} />
      <UpdateAgentDialog open={!!updateTarget} onOpenChange={(v) => !v && setUpdateTarget(null)} machine={updateTarget} />

      <Dialog open={!!renameTarget} onOpenChange={(v) => !v && setRenameTarget(null)}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading">Rename machine</DialogTitle>
            <DialogDescription className="text-zinc-400">
              This only changes the display name in your dashboard. The agent installed on the computer keeps running normally — nothing needs reinstalling.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="rename-machine-input"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmRename()}
            placeholder="Machine name"
            maxLength={60}
            autoFocus
            className="bg-zinc-950 border-white/10"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameTarget(null)} data-testid="cancel-rename" className="hover:bg-zinc-800">Cancel</Button>
            <Button onClick={confirmRename} disabled={renaming || !renameValue.trim()} data-testid="confirm-rename" className="bg-blue-600 hover:bg-blue-500">
              {renaming ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <AlertDialog open={!!regenTarget} onOpenChange={(v) => !v && setRegenTarget(null)}>
        <AlertDialogContent className="bg-zinc-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Regenerate pairing token?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              A new token will be issued for "{regenTarget?.name}" and the current agent will be disconnected immediately. Any old installer or leaked token stops working. You'll need to re-run the new installer on that machine.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-zinc-800" data-testid="cancel-regenerate">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRegenerate} className="bg-blue-600 hover:bg-blue-500" data-testid="confirm-regenerate">Regenerate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-zinc-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              {deleteTarget?.online ? `Remove and uninstall "${deleteTarget?.name}"?` : `"${deleteTarget?.name}" is offline`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {deleteTarget?.online ? (
                <>This will <strong className="text-zinc-200">stop the agent, remove its auto-start, and delete the Controlly folder</strong> from that computer, then remove it from your dashboard. This cannot be undone.</>
              ) : (
                <>We can't reach this computer right now to uninstall the agent. You can <strong className="text-zinc-200">schedule the uninstall</strong> (it stays listed and cleans itself up the moment it comes back online), or <strong className="text-zinc-200">remove it now</strong> — but then the agent stays installed on that PC and you'd have to uninstall it there manually.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-zinc-800" data-testid="cancel-delete">Cancel</AlertDialogCancel>
            {deleteTarget?.online ? (
              <AlertDialogAction onClick={() => confirmDelete()} className="bg-red-600 hover:bg-red-500" data-testid="confirm-delete">
                Remove &amp; uninstall
              </AlertDialogAction>
            ) : (
              <>
                <AlertDialogAction onClick={() => confirmDelete({ offline_action: "force" })} className="bg-transparent border border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="confirm-delete-force">
                  Remove now (leave agent)
                </AlertDialogAction>
                <AlertDialogAction onClick={() => confirmDelete({ offline_action: "schedule" })} className="bg-blue-600 hover:bg-blue-500" data-testid="confirm-delete-schedule">
                  Schedule uninstall
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
